import './LeadsSummary.css';
import ExcelJS from 'exceljs';
import { useEffect, useState } from 'react';
import CanvasJSReact from '@canvasjs/react-charts';
import { capitalizeFirstLetter } from '../../utilities/services/capitalizeFirstLetter';
import LeadsSummaryTasks from '../LeadsSummaryTasks/LeadsSummaryTasks';
import { preferenceOptions } from '../../utilities/services/preferencesOptions';
import { getAllLeads } from '../../utilities/api/leads';
 
const CanvasJSChart = CanvasJSReact.CanvasJSChart;

export default function LeadsSummary({leadsData, handleDropdownClick}) {
  const [annualData, setAnnualData] = useState([])
  const [leadsByProvince, setLeadsByProvince] = useState([])
  const [leadsBySource, setLeadsBySource] = useState([])
  const [leadsStatusData, setLeadsStatusData] = useState([])
  const [isExportingSheet, setIsExportingSheet] = useState(false)
  const [exportSheetColumns, setExportSheetColumns] = useState([])


  const leadsStatusDonutOptions = {
    animationEnabled: true,
    title: {
      // text: "Leads Distribution"
    },
    subtitles: [{
      text: "Leads Distribution",
      verticalAlign: "center",
      fontSize: 20,
      dockInsidePlotArea: true
    }],
    data: [{
      type: "doughnut",
      showInLegend: true,
      indexLabel: "{name}: {y}",
      yValueFormatString: "#,###'%'",
      dataPoints: leadsStatusData
    }]
  }

  const leadsByProvinceColumnChartOptions = {
    title: {
      // text: "Leads by Province"
    },
    toolTip: {
      shared: true,
      contentFormatter: function (e) {
        let content = ''
        e.entries.forEach(entry => {
          content += `<strong>${entry.dataPoint.label}</strong>: ${entry.dataPoint.y} ${entry.dataPoint.y === 1 ? 'Lead' : 'Leads'}<br>`
        })
        return content
      }
    },
    theme: "light1",
    dataPointMaxWidth: 50,
    colorSet: "colorSet1",
    axisY: {
      title: "Quantity",
      // suffix: "°F"
    },
    data: [{
      type: "column",
      dataPoints: [
      ...leadsByProvince
      ]
    }]
  }

  const leadsBySourcePieChartOptions = {
    exportEnabled: true,
    animationEnabled: true,
    title: {
      // text: "Leads by Source"
    },
    data: [{
      type: "pie",
      startAngle: 75,
      toolTipContent: "<b>{label}</b>: {y}%",
      showInLegend: "true",
      legendText: "{label}",
      indexLabelFontSize: 14,
      indexLabel: "{label} - {y}%",
      dataPoints: [
        ...leadsBySource
      ]
    }]
  }

  const areaChartOptions = {
    animationEnabled: true,
    theme: "light2",
    title: {
        // text: "Leads Added Monthly" 
    },
    axisX: {
        valueFormatString: "MMM",
        intervalType: "month",
        interval: 1
    },
    axisY: {
        title: "Quantity",
        // suffix: "°F"
    },
    toolTip: {
      shared: true,
      contentFormatter: function (e) {
        let content = ''
        e.entries.forEach(entry => {
          content += `<strong>${entry.dataSeries.name}</strong>: ${entry.dataPoint.y} ${entry.dataPoint.y === 1 ? 'Lead' : 'Leads'}<br>`
        })
        return content
      }
    },
    legend: {
        cursor: "pointer",
        itemclick: function (e) {
            if (typeof (e.dataSeries.visible) === "undefined" || e.dataSeries.visible) {
                e.dataSeries.visible = false;
            } else {
                e.dataSeries.visible = true;
            }
            e.chart.render();
        }
    },
    data: annualData
  };

  const handleExportSheetColumnChange = (evt) => {
    const column = evt.target.value
    if (exportSheetColumns.includes(column)) {
      setExportSheetColumns(exportSheetColumns.filter(item => item !== column))
    } else {
      setExportSheetColumns([...exportSheetColumns, column])
    }
  }

  const createExcelSheet = async () => {
    setIsExportingSheet(true)
    try {
      const leads = await getAllLeads()
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Leads');
      sheet.columns = [
        exportSheetColumns.includes('leadName') && { header: 'LEAD NAME', key: 'leadName', width: 20 },
        exportSheetColumns.includes('email') && { header: 'EMAIL', key: 'email', width: 20 },
        exportSheetColumns.includes('source') && { header: 'SOURCE', key: 'source', width: 20 },
        exportSheetColumns.includes('contactName') && { header: 'CONTACT NAME', key: 'contactName', width: 20 },
        exportSheetColumns.includes('phone') && { header: 'PHONE', key: 'phone', width: 20 },
        exportSheetColumns.includes('note') && { header: 'NOTES', key: 'note', width: 20 },
        exportSheetColumns.includes('status') && { header: 'STATUS', key: 'status', width: 20 },
        exportSheetColumns.includes('province') && { header: 'PROVINCE', key: 'province', width: 20 },
        exportSheetColumns.includes('address') && { header: 'ADDRESS', key: 'address', width: 20 },
        exportSheetColumns.includes('createdAt') && { header: 'CREATION', key: 'createdAt', width: 20 },
        exportSheetColumns.includes('updatedAt') && { header: 'LAST UPDATE', key: 'updatedAt', width: 20 }
      ];
      sheet.getRow(1).font = { bold: true, size: 14 };
      leads.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).forEach(lead => {
        sheet.addRow({
          leadName: capitalizeFirstLetter(lead.leadName),
          email: lead.email? lead.email: '---',
          source: lead.source? capitalizeFirstLetter(lead.source): '---',
          contactName: lead.contactName? capitalizeFirstLetter(lead.contactName): '---',
          phone: lead.phone? lead.phone: '---',
          note: lead.note? lead.note: '---',
          status: lead.status,
          province: lead.province? lead.province: '---',
          address: lead.address? capitalizeFirstLetter(lead.address): '---',
          createdAt: new Date(lead.createdAt).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }),
          updatedAt: new Date(lead.updatedAt).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
        })
      })
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Leads.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error)
    }
    setIsExportingSheet(false)
  }

  useEffect(() => {
    const currentDate = new Date();
    const tempAnnualData = leadsData.annualData.map(data => {
      return {
          type: "area",
          name: (data.year).toString(),
          showInLegend: true,
          xValueFormatString: "MMMM",
          yValueFormatString: "#,##0",
          // Use default year of 2024, so that line charts are aligned
          dataPoints: [
            { date: new Date(data.year, 0), x: new Date(2024, 0), y: data.monthlyData.JAN },
            { date: new Date(data.year, 1), x: new Date(2024, 1), y: data.monthlyData.FEB },
            { date: new Date(data.year, 2), x: new Date(2024, 2), y: data.monthlyData.MAR },
            { date: new Date(data.year, 3), x: new Date(2024, 3), y: data.monthlyData.APR },
            { date: new Date(data.year, 4), x: new Date(2024, 4), y: data.monthlyData.MAY },
            { date: new Date(data.year, 5), x: new Date(2024, 5), y: data.monthlyData.JUN },
            { date: new Date(data.year, 6), x: new Date(2024, 6), y: data.monthlyData.JUL },
            { date: new Date(data.year, 7), x: new Date(2024, 7), y: data.monthlyData.AUG },
            { date: new Date(data.year, 8), x: new Date(2024, 8), y: data.monthlyData.SEP },
            { date: new Date(data.year, 9), x: new Date(2024, 9), y: data.monthlyData.OCT },
            { date: new Date(data.year, 10), x: new Date(2024, 10), y: data.monthlyData.NOV },
            { date: new Date(data.year, 11), x: new Date(2024, 11), y: data.monthlyData.DEC }
          ].filter(dataPoint => currentDate >= dataPoint.date).map(dataPoint => {
            return {x: dataPoint.x, y: dataPoint.y}
          })
        }
    })
    setAnnualData(tempAnnualData)
  
    const tempLeadsByProvince = Object.keys(leadsData.leadsByProvince).map(province => {
      return {label: province, y: leadsData.leadsByProvince[province]}
    })
    setLeadsByProvince(tempLeadsByProvince)

    const totalLeadsMinusConverted = leadsData.totalActiveLeads + leadsData.totalHotLeads + leadsData.totalDeadLeads
    const totalLeads = leadsData.totalActiveLeads + leadsData.totalHotLeads + leadsData.totalDeadLeads + leadsData.totalConvertedLeads
    const tempLeadsStatusData = [
      { name: "Hot", y: Math.round(((leadsData.totalHotLeads)/totalLeadsMinusConverted)*100), color: "#E21D12" },
      { name: "Active", y: Math.round(((leadsData.totalActiveLeads)/totalLeadsMinusConverted)*100), color: "#1A5E9B" },
      { name: "Dead", y: Math.round(((leadsData.totalDeadLeads)/totalLeadsMinusConverted)*100), color: "#8993A1" }
    ]
    setLeadsStatusData(tempLeadsStatusData)

    const tempLeadsBySourceData = Object.keys(leadsData.leadsBySource).map(source => {
      return {label: capitalizeFirstLetter(source), y: Math.round((leadsData.leadsBySource[source])*100/totalLeads)}
    })
    setLeadsBySource(tempLeadsBySourceData)
  }, [leadsData])

  return (
    <div className='LeadsSummary'>
      <div className='exportLeadsContainer dropdown'>
        <button className='exportLeads' title='Export Leads to Spreadsheet' id='exportLeadsDropDownToggle' data-title='Export Leads' data-bs-toggle="dropdown" data-bs-auto-close='outside' aria-expanded="false">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="20px" fill='#FFFFFF'>
            <path d="M 28.90625 1.96875 C 28.863281 1.976563 28.820313 1.988281 28.78125 2 L 11.5 2 C 9.585938 2 8 3.558594 8 5.46875 L 8 43.90625 C 8 46.160156 9.867188 48 12.125 48 L 37.875 48 C 40.132813 48 42 46.160156 42 43.90625 L 42 15.1875 C 42.027344 15.054688 42.027344 14.914063 42 14.78125 L 42 14.5 C 42.007813 14.234375 41.90625 13.972656 41.71875 13.78125 L 30.21875 2.28125 C 30.027344 2.09375 29.765625 1.992188 29.5 2 L 29.1875 2 C 29.097656 1.976563 29 1.964844 28.90625 1.96875 Z M 11.5 4 L 28 4 L 28 12.34375 C 28 14.355469 29.644531 16 31.65625 16 L 40 16 L 40 43.90625 C 40 45.074219 39.054688 46 37.875 46 L 12.125 46 C 10.945313 46 10 45.074219 10 43.90625 L 10 5.46875 C 10 4.644531 10.660156 4 11.5 4 Z M 30 4.9375 L 39.0625 14 L 31.65625 14 C 30.722656 14 30 13.277344 30 12.34375 Z M 17 24 L 17 38 L 33 38 L 33 24 Z M 19 26 L 24 26 L 24 28 L 19 28 Z M 26 26 L 31 26 L 31 28 L 26 28 Z M 19 30 L 24 30 L 24 32 L 19 32 Z M 26 30 L 31 30 L 31 32 L 26 32 Z M 19 34 L 24 34 L 24 36 L 19 36 Z M 26 34 L 31 34 L 31 36 L 26 36 Z"/>
          </svg>
          <span>
            &nbsp;&nbsp;Export Leads
          </span>
        </button>
        <div className='dropdown-menu exportLeadsDropDown' aria-labelledby="exportLeadsDropDownToggle" onClick={handleDropdownClick}>
          <div className='description'>Select Fields to Export</div>
          <ul>
          { Object.keys(preferenceOptions['leads']).map((option, index) => (
            <li key={index} className='dropdown-item'>
              <div className='form-check'>
                  <input className='form-check-input' 
                    id={`leads-${option}-${index}`} 
                    type="checkbox" 
                    defaultChecked={false} 
                    value={option}
                    disabled={isExportingSheet}
                    onChange={handleExportSheetColumnChange} 
                  />
                  <label className='form-check-label' htmlFor={`leads-${option}-${index}`}>
                      {preferenceOptions['leads'][option]}
                  </label>
              </div>
            </li>
            ))}
          </ul>
          <div className='buttonContainer'>
            <button className='exportLeads' onClick={createExcelSheet} disabled={isExportingSheet || exportSheetColumns.length===0}>
              Export
            </button>
          </div>
        </div>
        
      </div>
      <div className='annualLeadsInfo'>
        <div className='title1'>Monthly Data</div>
        <CanvasJSChart options = {areaChartOptions} />
        {/* <div className='hideWatermark'></div> */}
      </div>
      <div className='group'>
        <div className='leadsDistribution'>
          {/* <div className='title1'>Leads Distribution</div> */}
          <CanvasJSChart options = {leadsStatusDonutOptions}/>
          {/* <div className='hideWatermark'></div> */}
        </div>
        <div className='leadsDistributionCount'>
          <div className='hotCount container'>
            <div className='title'>Hot Leads</div>
            <div className='num'>{leadsData.totalHotLeads}</div>
          </div>
          <div className='activeCount container'>
            <div className='title'>Active Leads</div>
            <div className='num'>{leadsData.totalActiveLeads}</div>
          </div>
          <div className='deadCount container'>
            <div className='title'>Dead Leads</div>
            <div className='num'>{leadsData.totalDeadLeads}</div>
          </div>
        </div>
      </div>
      <div className='group'>
        <div className='overdueTasks'>
          <div className='title1'>Overdue Tasks</div>
            <div className='totalContainer'>
              <div className='total'>{leadsData.overdueTasks}</div>
            </div>
        </div>
        <div className='pendingTasks'>
          <div className='title1'>Pending Tasks</div>
          <LeadsSummaryTasks taskList={leadsData.pendingTasks} />
        </div>
      </div>
      <div className='group'>
        <div className='leadsByProvince'>
          <div className='title1'>Province Distribution</div>
          <CanvasJSChart options = {leadsByProvinceColumnChartOptions}/>
          {/* <div className='hideWatermark'></div> */}
        </div>
        <div className='leadsBySource'>
          <div className='title1'>Source Distribution</div>
          <CanvasJSChart options = {leadsBySourcePieChartOptions}/>
          {/* <div className='hideWatermark'></div> */}
        </div>
      </div>
    </div>
  )
}
